import React from 'react';
import { Link } from 'react-router-dom';

const ServicesPage = () => {
  const services = [
    {
      icon: 'code',
      title: 'Custom Software Development',
      description: 'Tailored solutions built from the ground up to address your unique business challenges and drive competitive advantage.',
      features: [
        'Full-stack web applications',
        'Enterprise software systems',
        'API development & integration',
        'Legacy system modernization'
      ]
    },
    {
      icon: 'cloud',
      title: 'Cloud & DevOps',
      description: 'Scalable cloud infrastructure and automated pipelines that reduce costs, improve reliability, and accelerate delivery.',
      features: [
        'AWS, Azure, GCP architecture',
        'CI/CD pipeline setup',
        'Container orchestration (Kubernetes)',
        'Infrastructure as Code'
      ]
    },
    {
      icon: 'psychology',
      title: 'AI & Machine Learning',
      description: 'Intelligent systems that turn your data into actionable insights and automate complex decision-making processes.',
      features: [
        'Custom ML model development',
        'Natural language processing',
        'Computer vision solutions',
        'Predictive analytics'
      ]
    },
    {
      icon: 'phone_iphone',
      title: 'Mobile App Development',
      description: 'Beautiful, performant applications that engage users and drive results on both iOS and Android platforms.',
      features: [
        'Native iOS & Android apps',
        'Cross-platform (React Native, Flutter)',
        'App store optimization',
        'Ongoing maintenance & updates'
      ]
    },
    {
      icon: 'security',
      title: 'Security & Compliance',
      description: 'Protect your systems and data with enterprise-grade security practices and compliance frameworks.',
      features: [
        'Security audits & penetration testing',
        'HIPAA, SOC 2, GDPR compliance',
        'Secure code reviews',
        'Identity & access management'
      ]
    },
    {
      icon: 'support_agent',
      title: 'Technical Consulting',
      description: 'Strategic guidance from senior engineers to help you make the right technology decisions for your business.',
      features: [
        'Technology strategy & roadmapping',
        'Architecture reviews',
        'Team augmentation',
        'Technical due diligence'
      ]
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Discovery',
      description: 'We start by deeply understanding your business, users, and goals. This foundation ensures everything we build drives real value.'
    },
    {
      step: '02',
      title: 'Strategy',
      description: 'We design the optimal technical approach, create detailed specifications, and build a roadmap that aligns with your timeline and budget.'
    },
    {
      step: '03',
      title: 'Development',
      description: 'Our agile teams deliver in focused sprints with weekly demos, continuous testing, and regular stakeholder feedback.'
    },
    {
      step: '04',
      title: 'Launch & Scale',
      description: 'We ensure smooth deployment, provide training, and remain your partner for ongoing optimization and growth.'
    }
  ];

  const technologies = [
    { name: 'React', category: 'Frontend' },
    { name: 'Node.js', category: 'Backend' },
    { name: 'Python', category: 'Backend' },
    { name: 'AWS', category: 'Cloud' },
    { name: 'Azure', category: 'Cloud' },
    { name: 'Kubernetes', category: 'DevOps' },
    { name: 'PostgreSQL', category: 'Database' },
    { name: 'MongoDB', category: 'Database' },
    { name: 'TensorFlow', category: 'AI/ML' },
    { name: 'TypeScript', category: 'Language' },
    { name: 'Go', category: 'Backend' },
    { name: 'Docker', category: 'DevOps' }
  ];

  return (
    <main className="flex-grow">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-white/5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32 relative">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm text-sm font-medium text-gray-300 mb-6">
              <span className="material-symbols-outlined text-lg">build</span>
              Our Services
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Full-Stack Technology Solutions for 
              <span className="text-white/90"> Modern Businesses</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              From custom software development to AI implementation, we bring deep expertise across the entire technology stack to solve your most complex challenges.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-gray-500 font-semibold text-sm uppercase tracking-wider">What We Do</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
              Comprehensive Technology Services
            </h2>
            <p className="text-lg text-gray-600">
              We offer end-to-end solutions tailored to your specific needs, backed by senior engineers with deep industry experience.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="group bg-white p-8 rounded-2xl border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all duration-300">
                <div className="w-14 h-14 rounded-xl bg-gray-900 flex items-center justify-center text-white mb-6">
                  <span className="material-symbols-outlined text-2xl">{service.icon}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="material-symbols-outlined text-gray-500 text-lg">check_circle</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-gray-500 font-semibold text-sm uppercase tracking-wider">Our Process</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
              How We Work
            </h2>
            <p className="text-lg text-gray-600">
              A proven methodology refined over 150+ successful projects, designed to minimize risk and maximize value.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-bold text-gray-100 mb-4">{step.step}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
                {index < 3 && (
                  <div className="hidden lg:block absolute top-8 left-[80%] w-[40%] border-t-2 border-dashed border-gray-200"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-gray-500 font-semibold text-sm uppercase tracking-wider">Technology Stack</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
              We Work With the Best Tools
            </h2>
            <p className="text-lg text-gray-600">
              Our team is proficient in a wide range of modern technologies, allowing us to choose the right tool for every job.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {technologies.map((tech, index) => (
              <div 
                key={index}
                className="px-6 py-3 bg-gray-50 rounded-full border border-gray-100 hover:border-gray-300 hover:bg-gray-100 transition-colors duration-200"
              >
                <span className="font-semibold text-gray-700">{tech.name}</span>
                <span className="text-sm text-gray-400 ml-2">({tech.category})</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Philosophy */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-gray-500 font-semibold text-sm uppercase tracking-wider">Pricing</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-6">
                Transparent, Value-Based Pricing
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                We believe in fair, transparent pricing that reflects the value we deliver. No hidden fees, no surprises—just honest partnerships built on mutual success.
              </p>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                    <span className="material-symbols-outlined text-gray-700 text-2xl">payments</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Fixed-Price Projects</h4>
                    <p className="text-gray-600">Best for well-defined projects with clear scope. You know exactly what you'll pay upfront.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                    <span className="material-symbols-outlined text-gray-700 text-2xl">schedule</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Time & Materials</h4>
                    <p className="text-gray-600">Ideal for evolving projects. Pay for what you use with full transparency on hours and rates.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                    <span className="material-symbols-outlined text-gray-700 text-2xl">groups</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Dedicated Teams</h4>
                    <p className="text-gray-600">Embed our engineers with your team for ongoing development at predictable monthly costs.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-white p-8 lg:p-10 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Get a Custom Quote</h3>
              <p className="text-gray-600 mb-6">Every project is unique. Tell us about yours, and we'll provide a detailed proposal within 48 hours.</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-gray-700">
                  <span className="material-symbols-outlined text-gray-500 text-xl">check</span>
                  Free initial consultation
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <span className="material-symbols-outlined text-gray-500 text-xl">check</span>
                  Detailed project proposal
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <span className="material-symbols-outlined text-gray-500 text-xl">check</span>
                  Transparent pricing breakdown
                </li>
                <li className="flex items-center gap-2 text-gray-700">
                  <span className="material-symbols-outlined text-gray-500 text-xl">check</span>
                  No obligation to proceed
                </li>
              </ul>
              <Link
                to="/contact"
                className="w-full inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white bg-gray-900 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
              >
                Request a Quote
                <span className="material-symbols-outlined text-xl ml-2">arrow_forward</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Let's discuss how our services can help you achieve your goals. Schedule a free consultation with our team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-gray-900 bg-white rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
            >
              Start Your Project
              <span className="material-symbols-outlined text-xl ml-2">arrow_forward</span>
            </Link>
            <Link
              to="/case-studies"
              className="inline-flex items-center justify-center px-8 py-4 text-base font-semibold text-white border border-white/20 rounded-xl hover:bg-white/10 transition-all duration-200"
            >
              View Case Studies
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServicesPage;
